import { useCallback, useEffect, useState } from "react";
import ClipperLib from "clipper-lib";

const emptyResult = Object.freeze({
    isPointUnderTriangleArray: undefined,
    processPoint: undefined,
    pointsPositions: undefined,
    upscaledFieldArray: undefined,
    upscaledNumberU: undefined,
    upscaledNumberV: undefined,
    upscaledNumberW: undefined,
});

export const useOverview3dDisplayDataPreprocessing = (triangles, fieldArray, overviewSetting, overviewResult, calcArea) => {
    const { fieldLimit, fieldType = "PROTECTION_AREA_BLMSCHV" } = overviewSetting ?? {};
    const [{isPointUnderTriangleArray, processPoint, pointsPositions, upscaledFieldArray, upscaledNumberU, upscaledNumberV, upscaledNumberW}, setResult] = useState(emptyResult);
    const [preprocessingActive, setPreprocessingActive] = useState(false);
    const onResult = useCallback(message => {
        setPreprocessingActive(false);
        setResult(message);
    },[]);

    useEffect(() => {
        if(fieldArray !== undefined && overviewResult !== undefined) {
            if(fieldArray.length === 0 || triangles === undefined) {
                const array = fieldArray.map(() => false);
                setResult(array);
                setPreprocessingActive(false);
            } else {
                setResult(emptyResult);
                setPreprocessingActive(true);
                setTimeout(() => {
                    const result = preProcessData({triangles, fieldArray, fieldLimit, fieldType, overviewResult, calcArea});
                    onResult(result);
                },0);
            }
        }
    },[triangles, fieldArray, fieldLimit, fieldType, overviewResult, calcArea]);

    return {isPointUnderTriangleArray, processPoint, pointsPositions, upscaledFieldArray, upscaledNumberU, upscaledNumberV, upscaledNumberW, preprocessingActive};
};

 
const preProcessData = data => {
    const {triangles, fieldArray, fieldLimit, fieldType, overviewResult, calcArea} = data;
    const {numberU, numberV, numberW, sliceDist} = overviewResult;
    const {originX, originY, originZ,
        uaxisX, uaxisY, uaxisZ,
        vaxisX, vaxisY, vaxisZ} = calcArea;

    // Resolution increases by resolutionFactor^2
    const resolutionFactor = triangles.length < 12000 ? 3
        : triangles.length < 24000 ? 2
            : 1;

    // 1. Precompute triangle data
    const precomputedTriangles = triangles.map(triangle => {
        const [A, B, C] = triangle;

        const Ax = A.x, Ay = A.y, Az = A.z;
        const Bx = B.x, By = B.y, Bz = B.z;
        const Cx = C.x, Cy = C.y, Cz = C.z;

        // Bounding box
        const minX = Math.min(Ax, Bx, Cx);
        const maxX = Math.max(Ax, Bx, Cx);
        const minY = Math.min(Ay, By, Cy);
        const maxY = Math.max(Ay, By, Cy);

        // Vectors
        const ABx = Bx - Ax, ABy = By - Ay, ABz = Bz - Az;
        const ACx = Cx - Ax, ACy = Cy - Ay, ACz = Cz - Az;

        // Normal
        const nx = ABy * ACz - ABz * ACy;
        const ny = ABz * ACx - ABx * ACz;
        const nz = ABx * ACy - ABy * ACx;

        if (Math.abs(nz) < 1e-9) {
            return null; // Vertical triangle, ignore
        }

        // Dot products for barycentric
        // v0 = AC, v1 = AB
        const dot00 = ACx * ACx + ACy * ACy + ACz * ACz;
        const dot01 = ACx * ABx + ACy * ABy + ACz * ABz;
        const dot11 = ABx * ABx + ABy * ABy + ABz * ABz;

        const denom = dot00 * dot11 - dot01 * dot01;
        const invDenom = 1 / denom;

        return {
            Ax, Ay, Az,
            nx, ny, nz,
            minX, maxX, minY, maxY,
            ACx, ACy, ACz, // v0
            ABx, ABy, ABz, // v1
            dot00, dot01, dot11,
            invDenom
        };
    }).filter(t => t !== null);

    // 2. Filter out triangles for which the horizontal projection overlaps with the horizontal projection of the positive fieldArray points
    const minValToConsiderPoint = fieldType === "PROTECTION_AREA_BLMSCHV" || !fieldLimit ? 1 : fieldLimit;

    // Build union of 2D cell squares where value >= minValToConsiderPoint
    const clipperScale = 1000;
    const huX = uaxisX / (2 * (numberU - 1));
    const huY = uaxisY / (2 * (numberU - 1));
    const hvX = vaxisX / (2 * (numberV - 1));
    const hvY = vaxisY / (2 * (numberV - 1));

    const activeCellPaths = [];
    for (let v = 0; v < numberV; v++) {
        for (let u = 0; u < numberU; u++) {
            let isActive = false;
            for (let w = 0; w < numberW; w++) {
                if (fieldArray[w * numberV * numberU + v * numberU + u] >= minValToConsiderPoint) {
                    isActive = true;
                    break;
                }
            }
            if (!isActive) {
                continue;

            }
            const uFrac = u / (numberU - 1);
            const vFrac = v / (numberV - 1);
            const cx = originX + uaxisX * uFrac + vaxisX * vFrac;
            const cy = originY + uaxisY * uFrac + vaxisY * vFrac;
            activeCellPaths.push([
                {X: Math.round((cx - huX - hvX) * clipperScale), Y: Math.round((cy - huY - hvY) * clipperScale)},
                {X: Math.round((cx + huX - hvX) * clipperScale), Y: Math.round((cy + huY - hvY) * clipperScale)},
                {X: Math.round((cx + huX + hvX) * clipperScale), Y: Math.round((cy + huY + hvY) * clipperScale)},
                {X: Math.round((cx - huX + hvX) * clipperScale), Y: Math.round((cy - huY + hvY) * clipperScale)},
            ]);
        }
    }

    let unionPaths = [];
    if (activeCellPaths.length > 0) {
        const unionClipper = new ClipperLib.Clipper();
        unionClipper.AddPaths(activeCellPaths, ClipperLib.PolyType.ptSubject, true);
        const solution = new ClipperLib.Paths();
        unionClipper.Execute(ClipperLib.ClipType.ctUnion, solution, ClipperLib.PolyFillType.pftNonZero, ClipperLib.PolyFillType.pftNonZero);
        unionPaths = solution;
    }


    // Filter triangles to those whose XY projection intersects the active field union
    const filteredTriangles = unionPaths.length === 0 ? [] : precomputedTriangles.filter(tri => {
        const triPath = [
            {X: Math.round(tri.Ax * clipperScale), Y: Math.round(tri.Ay * clipperScale)},
            {X: Math.round((tri.Ax + tri.ABx) * clipperScale), Y: Math.round((tri.Ay + tri.ABy) * clipperScale)},
            {X: Math.round((tri.Ax + tri.ACx) * clipperScale), Y: Math.round((tri.Ay + tri.ACy) * clipperScale)},
        ];
        const c = new ClipperLib.Clipper();
        c.AddPaths(unionPaths, ClipperLib.PolyType.ptSubject, true);
        c.AddPaths([triPath], ClipperLib.PolyType.ptClip, true);
        const result = new ClipperLib.Paths();
        c.Execute(ClipperLib.ClipType.ctIntersection, result);
        return result.length > 0;
    });

    // 3. Increase result resolution

    const upscaledNumberU = numberU * resolutionFactor;
    const upscaledNumberV = numberV * resolutionFactor;
    const upscaledNumberW = numberW;

    // uAxis
    const uAx = uaxisX, uAy = uaxisY, uAz = uaxisZ;
    // vAxis
    const vAx = vaxisX, vAy = vaxisY, vAz = vaxisZ;

    // Normal = uAxis cross vAxis
    const nx = uAy * vAz - uAz * vAy;
    const ny = uAz * vAx - uAx * vAz;
    const nz = uAx * vAy - uAy * vAx;
    const nLen = Math.sqrt(nx*nx + ny*ny + nz*nz);

    // wAxis
    const scale = (upscaledNumberW - 1) * sliceDist / nLen;
    const wAx = nx * scale;
    const wAy = ny * scale;
    const wAz = nz * scale;

    // Steps
    const uStepX = upscaledNumberU > 1 ? uAx / (upscaledNumberU - 1) : 0;
    const uStepY = upscaledNumberU > 1 ? uAy / (upscaledNumberU - 1) : 0;
    const uStepZ = upscaledNumberU > 1 ? uAz / (upscaledNumberU - 1) : 0;

    const vStepX = upscaledNumberV > 1 ? vAx / (upscaledNumberV - 1) : 0;
    const vStepY = upscaledNumberV > 1 ? vAy / (upscaledNumberV - 1) : 0;
    const vStepZ = upscaledNumberV > 1 ? vAz / (upscaledNumberV - 1) : 0;

    const wStepX = upscaledNumberW > 1 ? wAx / (upscaledNumberW - 1) : 0;
    const wStepY = upscaledNumberW > 1 ? wAy / (upscaledNumberW - 1) : 0;
    const wStepZ = upscaledNumberW > 1 ? wAz / (upscaledNumberW - 1) : 0;


    const upscaledFieldArrayLen = upscaledNumberU * upscaledNumberV * upscaledNumberW;

    const upscaledFieldArray = new Uint8Array(upscaledFieldArrayLen);
    const pointsPositions = new Array(upscaledFieldArrayLen);
    const isPointUnderTriangleArray = new Array(upscaledFieldArrayLen);
    const processPoint = new Array(upscaledFieldArrayLen);

    const minPositiveValue = fieldType === "PROTECTION_AREA_BLMSCHV" || !fieldLimit ? 1 : fieldLimit;

    // 4. Calculate data for points

    let vX = originX, vY = originY, vZ = originZ;
    for(let v = 0; v < upscaledNumberV; v++) {
        let uX = vX, uY = vY, uZ = vZ;
        for(let u = 0; u < upscaledNumberU; u++) {
            let wX = uX, wY = uY, wZ = uZ;
            // filter out triangles for which the 2D boundary overlaps with with the 2D boundary of the points
            const trianglesWithinPointsBoundary = filteredTriangles.filter(tri => uX > tri.minX && uX < tri.maxX && uY > tri.minY && uY < tri.maxY);

            for(let w = 0; w < upscaledNumberW; w++) {
                const index = w * upscaledNumberV * upscaledNumberU + v * upscaledNumberU + u;

                // Calculate iterpolation for increased resolution
                // recalculate pixel -> array-index
                const uRaw = u / resolutionFactor;
                const vRaw = v / resolutionFactor;

                // clamp: be sure that we don't exceed the array length
                const uFloat = Math.max(0, Math.min(numberU - 1.0001, uRaw));
                const vFloat = Math.max(0, Math.min(numberV - 1.0001, vRaw));

                // position of the original fieldArray value points that surround the interpolated value point
                const u0 = Math.floor(uFloat);
                const u1 = u0 + 1;
                const v0 = Math.floor(vFloat);
                const v1 = v0 + 1;

                // weight for bilinear interpolation
                const weightU = uFloat - u0;
                const weightV = vFloat - v0;

                // values from the original fieldArray
                const val00 = fieldArray[w * numberV * numberU + v0 * numberU + u0];
                const val10 = fieldArray[w * numberV * numberU + v0 * numberU + u1];
                const val01 = fieldArray[w * numberV * numberU + v1 * numberU + u0];
                const val11 = fieldArray[w * numberV * numberU + v1 * numberU + u1];

                // bilinear interpolation
                // intepolation along the u axis
                const topInterp = val00 * (1 - weightU) + val10 * weightU;
                const bottomInterp = val01 * (1 - weightU) + val11 * weightU;

                // intepolation along the v axis
                const finalValue = topInterp * (1 - weightV) + bottomInterp * weightV;

                // assign preprocessed values
                upscaledFieldArray[index] = finalValue;
                const shouldProcessPoint = finalValue >= minPositiveValue;

                processPoint[index] = shouldProcessPoint;

                if(shouldProcessPoint) {
                    pointsPositions[index] = {x: wX, y: wY, z: wZ};

                    // Check if the point is under any triangle
                    let isUnder = false;
                    for (let i = 0; i < trianglesWithinPointsBoundary.length; i++) {
                        const tri = trianglesWithinPointsBoundary[i];

                        // Project
                        const APx = wX - tri.Ax;
                        const APy = wY - tri.Ay;
                        const APz = wZ - tri.Az;

                        const dotNAP = tri.nx * APx + tri.ny * APy + tri.nz * APz;
                        const t = dotNAP / tri.nz;

                        // Check if projected point is above the original point
                        // PprojZ = Pz - t
                        // We want PprojZ > Pz => Pz - t > Pz => -t > 0 => t < 0
                        if (t >= 0) {
                            continue;
                        }

                        // Barycentric check
                        // Pproj = P - t * (0,0,1) ? No, Pproj is on the plane.
                        // Pproj = (Px, Py, Pz - t)
                        // v2 = Pproj - A = (Px - Ax, Py - Ay, Pz - t - Az)
                        // v2 = (APx, APy, APz - t)

                        const v2x = APx;
                        const v2y = APy;
                        const v2z = APz - t;

                        const dot02 = tri.ACx * v2x + tri.ACy * v2y + tri.ACz * v2z;
                        const dot12 = tri.ABx * v2x + tri.ABy * v2y + tri.ABz * v2z;

                        const uBary = (tri.dot11 * dot02 - tri.dot01 * dot12) * tri.invDenom;
                        const vBary = (tri.dot00 * dot12 - tri.dot01 * dot02) * tri.invDenom;

                        if (uBary >= 0 && vBary >= 0 && uBary + vBary <= 1) {
                            isUnder = true;
                            break; // Found one triangle above, no need to check others
                        }
                    }
                    isPointUnderTriangleArray[index] = isUnder;

                } else {
                    pointsPositions[index] = undefined;
                    isPointUnderTriangleArray[index] = false;
                }

                wX += wStepX;
                wY += wStepY;
                wZ += wStepZ;
            }
            uX += uStepX;
            uY += uStepY;
            uZ += uStepZ;
        }
        vX += vStepX;
        vY += vStepY;
        vZ += vStepZ;
    }

    return {
        isPointUnderTriangleArray,
        processPoint,
        pointsPositions,
        upscaledFieldArray,
        upscaledNumberU,
        upscaledNumberV,
        upscaledNumberW,
    };
};